import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { push } from '@lagunovsky/redux-react-router'
import { compact, flatten, groupBy, isNil, isUndefined, some, sortBy, uniq } from 'lodash'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import CityAction from 'referencial/components/city/actions/CityAction'
import i18n from 'simple-react-i18n'
import { Grid } from '@mui/material'
import useTitle from 'utils/customHook/useTitle'
import useActions from 'utils/customHook/useActions'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import ProgressCard from 'components/card/ProgressCard'
import DashboardMapPanel from 'station/components/dashboard/component/DashboardMapPanel'
import DashboardStationsPanel from 'station/components/dashboard/component/DashboardStationsPanel'
import { MAP, STATION_LIST, STATION_TYPE_NAME } from 'station/constants/StationConstants'
import HomeAction from 'home/actions/HomeAction'
import { getUserBookmarksByStationType } from 'utils/UserUtil'
import { findStationType, getBookmarks, getStationType, hasLocalisationStation } from 'utils/StationUtils'
import { getLabel, getObjectLabel } from 'utils/StoreUtils'
import { getDate } from 'utils/DateUtil'
import { normalizeString } from 'utils/StringUtil'
import { hasValue } from 'utils/NumberUtil'
import PluviometryAction from 'pluviometry/actions/PluviometryAction'
import { componentHasHabilitations } from 'utils/HabilitationUtil'
import { H_PLUVIO_DASHBOARD } from 'account/constants/AccessRulesConstants'
import UserAction from 'administration/components/user/actions/UserAction'
import StationAction from 'station/actions/StationAction'
import ContributorAction from 'referencial/components/contributor/actions/ContributorAction'
import QualityAction from 'quality/actions/QualityAction'
import ReferencialAction from 'referencial/action/ReferencialAction'
import PluviometersFilterFields from './PluviometersFilterFields'
import NetworkAction from 'referencial/components/network/actions/NetworkAction'
import useApplicationSetting, { intParser } from 'utils/customHook/useApplicationSetting'
import useLocalStorage from 'utils/customHook/useLocalStorage'
import { DEFAULT_CONTRIBUTOR_TYPE } from 'administration/components/user/constants/UserConstants'
import AddPluvioStepper from '../addPluvio/AddPluvioStepper'
import SimpleTabList from 'components/list/SimpleTabList'
import Select from 'components/forms/Select'
import { convertCoordinates, PROJECTIONS } from 'utils/mapUtils/CoordinateUtils'
import ExportFileModal from 'components/modal/ExportFileModal'
import ExportAction from 'export/actions/ExportAction'
import ToastrAction from 'toastr/actions/ToastrAction'
import { formatData } from 'utils/ExportDataUtil'
import useBoolean from 'utils/customHook/useBoolean'
import { FR } from 'referencial/constants/ReferencialConstants'
import CampaignPortletPanel from 'campaign/components/CampaignPortletPanel'
import CampaignAction from 'campaign/actions/CampaignAction'
import { getCampaignIcon, getCampaignNameCell, getCampaignProgress, getCampaignsWithBookmarks, TableProgressBar } from 'campaign/utils/CampaignUtils'
import useCampaignBookmarks from '../../../utils/customHook/useCampaignBookmarks'
import { getColorCampaign } from 'utils/CampaignUtil'
import SimpleKeyFigurePanel from 'components/keyFigures/SimpleKeyFigurePanel'
import WifiIcon from '@mui/icons-material/Wifi'
import moment from 'moment'

const DEFAULT_FILTER = -1
const SEARCH_VALUE_HEADERS = ['number', 'code', 'name', 'city', 'SISEeaucode', 'creationDate', 'stationTypeLabel', 'operator', 'administrator', 'referent', 'department']
const stationType = STATION_TYPE_NAME.pluviometry
const PLUVIOMETRY_HEADERS_EXPORT = ['code', 'cityCode', 'internalReferences', 'cityLabel', 'name', 'creationDate', 'closeDate', 'administrator']
const CAMPAIGN_HEADERS = ['qualificationTableColor', 'nullValue', 'name', 'nbStations', 'startDate', 'progression', 'referent']

const PLUVIO_DASHBOARD_FILTERS = 'PLUVIO_DASHBOARD_FILTERS'
const DEFAULT_FILTERS = {
    filter: DEFAULT_FILTER,
    filterResults: [],
    referentIds: [],
    operatorIds: [],
    network: undefined,
    searchValue: '',
}

const sanitizeFilters = (f = {}) => ({
    filter: f.filter ?? DEFAULT_FILTER,
    filterResults: (f.filterResults ?? []).map(({ id }) => ({ id })),
    referentIds: f.referentIds ?? [],
    operatorIds: f.operatorIds ?? [],
    network: f.network,
    searchValue: f.searchValue ?? '',
})

const ExportModal = ({
    isOpen,
    onClose = () => {},
    filteredPluviometers = [],
}) => {
    const dispatch = useDispatch()
    const citiesIndex = useSelector(store => store.CityReducer.citiesIndex, shallowEqual)

    const [projection, setProjection] = useState()

    const getLocalisation = useCallback(pluvio => {
        if (hasLocalisationStation(pluvio)) return { x: pluvio.x, y: pluvio.y, projection: pluvio.projection }

        const city = citiesIndex[pluvio.townCode]
        if (city && (!city.countryCode || city.countryCode === FR) && hasLocalisationStation(city)) {
            return { x: city.x, y: city.y, projection: city.projection }
        }

        return {}
    }, [citiesIndex])


    const onExport = (type) => {
        const pluviometersFormated = filteredPluviometers.map(pluvio => {
            const loc = getLocalisation(pluvio)
            const usedLoc = !isNil(projection) ? convertCoordinates(loc.projection, projection, loc) : loc

            return {
                ...pluvio,
                x: { value: usedLoc.x, format: '0.0000000000', cellType: 'number' },
                y: { value: usedLoc.y, format: '0.0000000000', cellType: 'number' },
                projection: { value: projection ?? usedLoc.projection, format: '0', cellType: 'number' },
            }
        })

        if (pluviometersFormated.length) {
            const exportData = [
                {
                    ...pluviometersFormated[0],
                    headers: [...pluviometersFormated[0].headers, 'x', 'y', 'projection'],
                },
                ...pluviometersFormated.slice(1),
            ]
            dispatch(ExportAction.export(formatData(exportData), type, i18n.watchpoints))
        } else {
            dispatch(ToastrAction.warning(i18n.noDataToExport))
        }
    }

    return (
        <ExportFileModal
            open={isOpen}
            onClose={onClose}
            data={[
                {
                    name: i18n.resultsTable,
                    formats: [{
                        type: i18n.excelFile,
                        callback: () => onExport('xlsx'),
                    }, {
                        type: i18n.csvFile,
                        callback: () => onExport('csv'),
                    }],
                    settingsContent: (
                        <Select
                            label={i18n.coordinateConversion}
                            value={projection}
                            onChange={setProjection}
                            options={PROJECTIONS}
                            keyLabel='name'
                            keyValue='sandreCode'
                        />
                    ),
                },
            ]}
        />
    )
}

ExportModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    filteredPluviometers: PropTypes.arrayOf(PropTypes.shape({})),
}

const PluviometryPanel = () => {
    const PLUVIOMETRY_HEADERS = ['code',
        { key: 'cityCode', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.cityCodeHeader}</span>) },
        { key: 'internalReferences', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.internalReferencesHeader}</span>) },
        'cityLabel', 'name', 'creationDate', 'closeDate', 'administrator']

    const {
        status,
        citiesIndex,
        pluviometers,
        pluviometryCampaigns,
        pluviometryCampaignsProgress,
        pluviometryKeyfigures,
        userBookmarks,
        globalResearch,
        contributorLinks,
        contributorsIndex,
        networkLinks,
        networks,
        contacts,
        accountUser,
    } = useSelector(store => ({
        citiesIndex: store.CityReducer.citiesIndex,
        pluviometers: store.PluviometryReducer.pluviometers,
        pluviometryCampaigns: store.PluviometryReducer.pluviometryCampaigns,
        pluviometryCampaignsProgress: store.PluviometryReducer.pluviometryCampaignsProgress,
        pluviometryKeyfigures: store.PluviometryReducer.pluviometryKeyfigures,
        userBookmarks: store.UserReducer.userBookmarks,
        globalResearch: store.HomeReducer.globalResearch,
        contributorLinks: store.StationReducer.contributorLinks,
        contributorsIndex: store.ContributorReducer.contributorsIndex,
        networkLinks: store.PluviometryReducer.networkPluvioLink,
        networks: store.NetworkReducer.networks,
        contacts: store.ContactReducer.contacts,
        accountUser: store.AccountReducer.accountUser,
        status: store.QualityReducer.status,
    }), shallowEqual)

    const operatorType = useApplicationSetting('contributorTypeOperator', intParser) || DEFAULT_CONTRIBUTOR_TYPE.OPERATOR
    const referentType = useApplicationSetting('contributorTypeAdministrator', intParser) || DEFAULT_CONTRIBUTOR_TYPE.ADMINISTRATOR

    const [isOpen, setIsOpen] = useState(false)
    const [filters, setStoredFilters] = useLocalStorage(
        PLUVIO_DASHBOARD_FILTERS,
        () => sanitizeFilters({ ...DEFAULT_FILTERS, searchValue: globalResearch || '' }),
        stored => sanitizeFilters({ ...stored, searchValue: globalResearch || stored.searchValue }),
    )

    const setFilters = useCallback(next => setStoredFilters(sanitizeFilters(next)), [setStoredFilters])

    const {
        value: isExportOpen,
        setTrue: onOpenExport,
        setFalse: onCloseExport,
    } = useBoolean(false)

    const dispatch = useDispatch()

    useEffect(() => {
        if (globalResearch) {
            dispatch(HomeAction.updateGlobalResearch(''))
        }
    }, [])

    const {
        isLoaded: isCampaignsLoaded,
        progress: campaignsProgress,
    } = useProgressDispatch(() => [
        dispatch(CampaignAction.fetchPluviometryCampaigns()),
        dispatch(CampaignAction.fetchPluviometryCampaignsProgress()),
    ], [])

    const {
        isLoaded: isStatsLoaded,
    } = useProgressDispatch(() => [
        dispatch(PluviometryAction.fetchKeyFigures()),
    ], [])

    const { isLoaded: isLoadedRef, progress: progressRef } = useProgressDispatch(() => {
        const promises = !userBookmarks.length ? [UserAction.fetchBookmarks] : []
        const findedStationType = findStationType(stationType)
        const promisesContributorsLinks = [...promises, () => StationAction.fetchAllContributors(findedStationType.code)]
        const promisesContributors = [...promisesContributorsLinks, ContributorAction.fetchContributors]
        const promisesStatus = !status.length ? [...promisesContributors, QualityAction.fetchStatus] : promisesContributors
        const promisesSandreCodes = [...promisesStatus, ReferencialAction.fetchSandreCodes]
        const promisesCities = [...promisesSandreCodes, CityAction.fetchCities]
        return promisesCities.map(p => dispatch(p()))
    }, [])

    const referentLinksByStation = useMemo(() => groupBy(contributorLinks.filter(l => l.contributorType === referentType), 'idStation'), [contributorLinks, referentType])
    const now = moment().valueOf()

    const pluviometersFormatted = useMemo(() => {
        if (pluviometers.length) {
            return sortBy(pluviometers, o => o.name ? o.name.toUpperCase() : '}').map(s => ({
                ...s,
                nullValue: hasValue(s.jobExecutionId) && <WifiIcon />,
                bookmark: getBookmarks(s.code, getUserBookmarksByStationType(userBookmarks, 'pluviometry', s.code)),
                city: { value: getObjectLabel(citiesIndex[s.townCode], 'labelWithCode') },
                name: s.name || '',
                cityCode: s.townCode,
                cityLabel: getObjectLabel(citiesIndex[s.townCode], 'name'),
                creationDate: { value: getDate(s.creationDate), sortValue: s.creationDate },
                closeDate: { value: getDate(s.closeDate), sortValue: s.closeDate },
                stationType: s.stationType ? getStationType(parseInt(s.stationType)).libelle : '',
                administrator: uniq(compact((referentLinksByStation[s.id]?.filter(link => link.endDate >= now || !link.endDate) ?? []).map(l => contributorsIndex[l.idContributor]?.name))).join(', '),
                headers: PLUVIOMETRY_HEADERS_EXPORT,
            }))
        }
        return []
    }, [citiesIndex, contributorsIndex, pluviometers, referentLinksByStation, userBookmarks])

    const containsSearchValue = useCallback((station) =>
        some(SEARCH_VALUE_HEADERS, prop => station[prop] ?
            normalizeString(station[prop].toString()).includes(normalizeString(filters.searchValue))
            : false)
    , [filters.searchValue])

    const data = useMemo(() => {
        const searchFiltered = pluviometersFormatted.filter(s => containsSearchValue(s))

        const defaultResult = {
            title: i18n.watchpoints,
        }

        const operatorFiltered = !filters.operatorIds?.length ?
            searchFiltered :
            searchFiltered.filter(s => contributorLinks.filter(l => l.contributorType === operatorType && filters.operatorIds.includes(l.idContributor) && (l.endDate >= now || !l.endDate)).some(l => l.idStation === s.id))
        const referentFiltered = !filters.referentIds?.length ?
            operatorFiltered :
            operatorFiltered.filter(s => contributorLinks.filter(l => l.contributorType === referentType && filters.referentIds.includes(l.idContributor) && (l.endDate >= now || !l.endDate)).some(l => l.idStation === s.id))

        const networkFiltered = !isUndefined(filters.network) ? networkLinks
            .filter(l => filters.network === l.idNetwork && (l.endDate >= now || !l.endDate))
            .map(l => referentFiltered.find(s => s.id === l.idPluviometer))
            .filter(s => !!s) : referentFiltered

        if (hasValue(filters.filter) && filters.filter !== DEFAULT_FILTER) {
            return { ...defaultResult, stations: flatten(filters.filterResults.map(stationResult => networkFiltered.find(station => station.id === stationResult.id) || [])) }
        }

        return { ...defaultResult, stations: networkFiltered }
    }, [pluviometersFormatted, filters.filter, filters.filterResults, filters.referentIds, filters.operatorIds, filters.network, containsSearchValue, contributorLinks, operatorType, referentType, networkLinks])

    useActions(() => {
        const defaultActions = {
            exportList: [{
                onClick: onOpenExport,
                label: i18n.excel,
            }],
        }
        const newActions = (accountUser.isAdmin === '1' || accountUser.metadata === '1') ? {
            ...defaultActions,
            new: () => setIsOpen(true),
        } : defaultActions
        return newActions
    }, [data])

    const pluvioNetworks = uniq(networkLinks.filter(n => !n.endDate || n.endDate >= now).map(l => l.idNetwork)).map(id => networks.find(n => id === n.id)).filter(c => !isUndefined(c))

    const { bookmarkIds: campaignBookmarkIds } = useCampaignBookmarks(STATION_TYPE_NAME.pluviometry)

    const campaigns = useMemo(() => {
        const currentCampaigns = pluviometryCampaigns.reduce((acc, c) => {
            const { nbStation = 0, nbStationValidated = 0 } = pluviometryCampaignsProgress.find(cp => cp.id === c.id) || {}

            if (nbStation !== 0 && nbStationValidated < nbStation && c.statut === 2) {
                return [
                    ...acc,
                    c,
                ]
            } return acc
        }, [])

        return getCampaignsWithBookmarks(pluviometryCampaigns, currentCampaigns, campaignBookmarkIds).map(campaign => {
            const {
                nbStation,
                progress: campaignProgress = 0,
                progressTotal = 0,
            } = pluviometryCampaignsProgress.find(p => p.id === campaign.id) || {}

            const progressValue = getCampaignProgress(campaignProgress, progressTotal)
            return {
                id: campaign.id,
                qualificationTableColor: {
                    color: getColorCampaign(false, progressValue, 2),
                },
                nullValue: getCampaignIcon(campaign.campaignType, 'small'),
                name: getCampaignNameCell(campaign.name, campaignBookmarkIds.includes(campaign.id)),
                nbStations: nbStation,
                startDate: campaign.beginningApplication,
                progression: <TableProgressBar nb={campaignProgress} nbTotal={progressTotal} progress={progressValue} labelSing={i18n.analyseOn} labelPlur={i18n.analysisOn} />,
                referent: getLabel(contacts, campaign.contactCode, 'mnemonique'),
                contactCode: campaign.contactCode,
            }
        })
    }, [contacts, pluviometryCampaigns, pluviometryCampaignsProgress, campaignBookmarkIds])

    return (
        <>
            <Grid container spacing={1}>
                <Grid container size={12}>
                    <Grid size={9}>
                        {isCampaignsLoaded ? (
                            <CampaignPortletPanel
                                campaigns={campaigns}
                                stationType={stationType}
                                headers={CAMPAIGN_HEADERS}
                            />
                        ) : <ProgressCard progress={campaignsProgress} withMessage />}
                    </Grid>
                    <Grid size={3}>
                        <SimpleKeyFigurePanel
                            data={[
                                ...pluviometryKeyfigures,
                                { title: 'stations', value: pluviometers.length },
                            ]}
                            loading={!isStatsLoaded}
                        />
                    </Grid>
                </Grid>
                <Grid size={12}>
                    <SimpleTabList
                        defaultTab={STATION_LIST}
                        tabs={[{
                            constant: STATION_LIST,
                            label: i18n.table,
                            icon: 'list',
                        },
                        {
                            constant: MAP,
                            label: i18n.map,
                            icon: 'map',
                        }]}
                        noPadding
                        headerComponent={isLoadedRef ? (
                            <PluviometersFilterFields
                                defaultFilter={filters}
                                onValidate={setFilters}
                                stationType={stationType}
                                stations={pluviometersFormatted}
                                contributorLinks={contributorLinks}
                                networklist={pluvioNetworks}
                            />
                        ) : <ProgressCard progress={progressRef} withMessage />}
                    >
                        {tab => (
                            <>
                                {tab === STATION_LIST && <DashboardStationsPanel stationType={stationType} data={data} headers={[{ key: 'bookmark', value: '' }, { key: 'nullValue', value: '' }, ...PLUVIOMETRY_HEADERS]} />}
                                {tab === MAP && <DashboardMapPanel stationType={stationType} data={data} />}
                            </>
                        )}
                    </SimpleTabList>
                </Grid>
                <AddPluvioStepper
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                />
                <ExportModal
                    isOpen={isExportOpen}
                    onClose={onCloseExport}
                    filteredPluviometers={data.stations.map((s) => ({ ...s, code: { value: s.code, cellType: 'string' }, cityCode: { value: s.cityCode, cellType: 'string' } }))}
                />
            </Grid>
        </>
    )
}

PluviometryPanel.propTypes = {
    stationType: PropTypes.string,
}

const PluviometersDashboard = () => {
    const dispatch = useDispatch()

    useEffect(() => {
        if (!componentHasHabilitations(H_PLUVIO_DASHBOARD)) {
            dispatch(push('/unauthorized'))
        }
    }, [dispatch])

    const { isLoaded, progress } = useProgressDispatch(() => [
        dispatch(PluviometryAction.fetchPluviometers()),
        dispatch(PluviometryAction.fetchNetworkPluvioLink()),
        dispatch(NetworkAction.fetchNetworks()),
    ], [])

    useTitle(() => [{
        title: i18n[stationType],
        href: stationType,
    }, {
        title: i18n.dashboard,
        href: stationType,
    }], [stationType])

    return isLoaded ? <PluviometryPanel /> : <ProgressCard progress={progress} withMessage />
}

export default PluviometersDashboard
